import { DateTime } from 'luxon';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { RESULT } from 'shared/api/query/constants';
import {
  doConfirmRegisterCode,
  doGetPublicAccountData,
  doLogoutUser,
  doOAuthLogin, doOAuthRegister, doRegisterViaEmail, dogma,
} from '@/api';
import { removeCookie } from 'shared/lib/cookie';
import { resetState } from '@/nebula/actions';

export const oauthRegister = ({ commit }, payload) => doOAuthRegister(payload)
  .then((response) => {
    if (response.status !== 200) {
      return response;
    }

    commit('REGISTER_URLS', response.data);
    return response;
  });

export const oauthLogin = ({ commit }, payload) => doOAuthLogin(payload)
  .then((response) => {
    if (response.status !== 200) {
      return response;
    }

    commit('LOGIN_URLS', response.data);
    return response;
  });

export const logout = ({ dispatch }) => doLogoutUser().then((response) => {
  dispatch('resetSession');
  return response;
});

export const resetSession = ({ commit }) => {
  removeCookie('foreign_login');
  commit('RESET_STATE');
  resetState({ commit });
  commit('USER_NOT_LOGGED_IN');
  commit('UNSET_FOREIGN_LOGIN');
  EventBus.$emit(EVENTS.SESSION.LOGOUT);
};

export const registerViaEmail = (_, payload) => doRegisterViaEmail(payload)
  .then((response) => response);

export const confirmRegisterCode = (_, payload) => doConfirmRegisterCode(payload)
  .then((response) => response);

export const getPublicAccountData = ({ commit }, { signUpPathName }) => doGetPublicAccountData(signUpPathName).then((response) => {
  if (response.status !== 200 && response.data.indexOf('could not find account') > -1) {
    commit('PUBLIC_ACCOUNT_DATA_RETRIEVED', { publicAccountData: null });
    return response;
  }

  if (response.status !== 200) {
    return response;
  }

  commit('PUBLIC_ACCOUNT_DATA_RETRIEVED', { publicAccountData: response.data });
  return response;
});

export const getEntity = ({ commit }, { entityById, id, model, mutation = '' }) => dogma.query(
  entityById(id),
)
  .then((response) => {
    if (response.status !== 200) {
      return response;
    }

    if (mutation !== '') {
      commit(mutation, { entity: response.data[RESULT][0], model });
      return response;
    }

    commit('ENTITY_RETRIEVED', { entity: response.data[RESULT][0], model });
    return response;
  });

export const getEntities = ({ commit }, { query, model, mutation = '' }) => dogma.query(
  query,
)
  .then((response) => {
    if (response.status !== 200) {
      return response;
    }

    if (mutation !== '') {
      commit(mutation, { entities: response.data[RESULT], model });
      return response;
    }

    commit('ENTITIES_RETRIEVED', { entities: response.data[RESULT], key: `${model}s` });

    return response;
  });

export const getEntitiesV2 = ({ commit }, { queries, model, mutation = '', options = {}, silent = false, headerOpts = null }) => {
  let header = {};
  if (headerOpts !== null) {
    header = headerOpts;
  }

  if (silent) {
    header = {
      Silent: 'true',
      ...header,
    };
  }
  return dogma.query(queries, { header })
    .then((response) => {
      if (response.status !== 200) {
        return response;
      }

      if (mutation !== '') {
        commit(mutation, { data: response.data, model, options });
        return response;
      }

      commit('ENTITIES_RETRIEVED_V3', { data: response.data, model, options });

      return response;
    });
};

export const deleteEntity = ({ commit }, { entity, model, mutation = '', headerOpts = {} }) => {
  const deletedAt = DateTime.local().toISO();
  return dogma.updateSingle(
    {
      ...entity,
      deletedAt,
    },
    model,
    [],
    null,
    { header: headerOpts },
  )
    .then((response) => {
      if (response.status !== 200) {
        return response;
      }

      if (mutation !== '') {
        commit(mutation, { entity, model });
        return response;
      }

      commit('ENTITY_DELETED', { entity, model });
      return response;
    });
};

export const deleteEntities = ({ commit }, { entities, model, mutation = '', headerOpts = {} }) => {
  const deletedAt = DateTime.local().toISO();
  return dogma.update(
    entities.map((e) => ({
      ...e,
      deletedAt,
    })),
    model,
    [],
    null,
    { header: headerOpts },
  )
    .then((response) => {
      if (response.status !== 200) {
        return response;
      }

      if (mutation !== '') {
        commit(mutation, { entities, model });
        return response;
      }

      commit('ENTITIES_DELETED', { entities, model });
      return response;
    });
};

export const restoreEntity = ({ commit }, { entity, model, mutation = '', options, headerOpts = {} }) => dogma.updateSingle(
  {
    ...entity,
    softDeletedAt: null,
  },
  model,
  [],
  null,
  { header: headerOpts },
)
  .then((response) => {
    if (response.status !== 200) {
      throw new Error('failed to restoreEntity');
    }

    if (mutation !== '') {
      commit(mutation, { entity, model, options });
      return response;
    }
    return response;
  });

export const restoreEntities = ({ commit }, { entities, model, mutation = '', options, headerOpts = {} }) => dogma.update(
  entities.map((e) => ({
    ...e,
    softDeletedAt: null,
  })),
  model,
  [],
  null,
  { header: headerOpts },
)
  .then((response) => {
    if (response.status !== 200) {
      throw new Error('failed to restoreEntities');
    }

    if (mutation !== '') {
      commit(mutation, { entities, model, options });
      return response;
    }
    return response;
  });

export const bulkMutate = ({ commit }, bulkPayloads) => dogma.bulkMutate(
  bulkPayloads.map((payload) => ({
    alias: payload.alias,
    nodes: payload.nodes,
    attributes: payload.attributes,
    model: payload.model,
    updateHookParameter: payload.updateHookParameter,
    createHookParameter: payload.createHookParameter,
  })),
).then((bulkResponse) => {
  if (bulkResponse.status !== 200) {
    return bulkResponse;
  }

  bulkPayloads.forEach(({ mutation = '', model, nodes, alias }) => {
    const response = { data: bulkResponse.data[alias] };

    if (mutation !== '') {
      commit(mutation, { entities: response.data });
      return;
    }

    const deletedNodes = nodes.filter((n) => n.deletedAt !== undefined);
    if (deletedNodes.length > 0) {
      commit('ENTITIES_DELETED', { entities: deletedNodes, model });
    }

    commit('ENTITIES_RETRIEVED_V2', { entities: response.data, model });
  });

  return bulkResponse;
});
